import { createRef, Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import flow from 'lodash/flow';
import classNames from 'clsx';

import {
  closeNowPlayingDialogAndTrack,
  closeNowPlayingDialog,
  fetchNowPlayingItems,
} from 'src/common/actions/dialog';
import {
  selectIsExpiredWebSubscriber,
  selectIsUserSubscribed,
} from 'src/common/selectors/me';
import { selectIsPrerollLoaded } from 'src/common/selectors/preroll';
import { selectUserAgent } from 'src/common/selectors/app';
import {
  selectCanChangePlaybackRate,
  selectCurrentlyPlayingGuideId,
  selectNowPlaying,
  selectNowPlayingUpsell,
  selectPlayerStatus,
} from 'src/common/selectors/player';
import { selectNowPlayingGuideItem } from 'src/common/selectors/dialog';
import CaretDown from '../../../components/shared/svgIcons/CaretDown';
import ImageWithDefault from '../../../components/shared/ImageWithDefault';
import Scrubber from '../../player/controls/scrubber/Scrubber';
import PlayButton from '../../player/controls/playButton/PlayButton';
import FavoriteControl from '../../player/controls/favorite/FavoriteControl';
import NowPlayingDialogAd from '../../ads/NowPlayingDialogAd';
import UpsellRibbon from '../../../components/subscription/UpsellRibbon';
import SpeedControl from '../../../components/player/actionDrawer/SpeedControl';
import ClampText from '../../shared/ClampText';

import connectWithExperiments from '../../../decorators/connectWithExperiments';
import connectWithAuth from '../../../decorators/auth/connectWithAuth';
import { is360 } from '../../../utils/breakpoints';

import { PAUSE_PREROLL_ENABLED } from '../../../constants/experiments/ads';
import { ENABLE_PLAYBACK_SPEED_CONTROL } from '../../../constants/experiments/player';
import {
  PREROLL_SUBTITLE,
  PREROLL_TITLE,
  ERROR_MESSAGE,
} from '../../../constants/localizations/player';
import playerStatuses from '../../../utils/playerStatuses';

import cssVars from '../../../styles/variables';
import createSubscribeLink from '../../../utils/subscription/createSubscribeLink';
import getNowPlayingContainerItemGuideId from '../utils/getNowPlayingContainerItemGuideId';
import { LocationAndLocalizationContext } from '../../../providers/LocationAndLocalizationProvider';
import { selectIsDesktopNowPlayingEnabled } from '../../../selectors/config';
import css from './now-playing.module.scss';

const SOURCE = 'nowPlaying';
const idPrefix = 'nowPlayingDialog';

const getTitle = (nowPlaying) => nowPlaying?.title || nowPlaying?.header?.title;
const getSubtitle = (nowPlaying) =>
  nowPlaying?.subtitle || nowPlaying?.header?.subtitle;

// exported for testing
export class NowPlaying extends Component {
  static propTypes = {
    // from parent
    matchUrl: PropTypes.string.isRequired,
    breakpoint: PropTypes.number.isRequired,
    hideDisplayAd: PropTypes.bool,

    // from mapDispatchToProps
    nowPlayingGuideItem: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    userAgent: PropTypes.string.isRequired,
    nowPlaying: PropTypes.object.isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    isPrerollLoaded: PropTypes.bool.isRequired,
    playerStatus: PropTypes.string.isRequired,
    selectedContentId: PropTypes.string,
    upsell: PropTypes.object.isRequired,
    canShowUpsell: PropTypes.bool.isRequired,
    isUserSubscribed: PropTypes.bool.isRequired,
    canChangePlaybackRate: PropTypes.bool.isRequired,
    currentlyPlayingGuideId: PropTypes.string.isRequired,
    isExpiredWebSubscriber: PropTypes.bool.isRequired,
    // from connectWithExperiments
    [PAUSE_PREROLL_ENABLED]: PropTypes.bool.isRequired,
    [ENABLE_PLAYBACK_SPEED_CONTROL]: PropTypes.bool,
    isDesktopNowPlayingEnabled: PropTypes.bool.isRequired,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props) {
    super(props);

    this.state = {
      isTitleScrolling: false,
      isSubtitleScrolling: false,
    };
    this.handleClose = this.handleClose.bind(this);
    this.startScrolling = this.startScrolling.bind(this);
    this.subtitleOuterContainer = createRef();
    this.subtitleInnerContainer = createRef();
    this.titleOuterContainer = createRef();
    this.titleInnerContainer = createRef();
  }

  componentDidMount() {
    const { actions, currentlyPlayingGuideId, nowPlayingGuideItem } =
      this.props;

    // NOTE: We only want to fetch new nowPlayingItems in the NowPlaying child component under specific circumstances
    // i.e. With the NowPlayingDialog open, the user clicks to tune a new station listed in one of the avail containers
    // If the currentlyPlayingGuideId differs from the guideId in the NowPlaying containerItem, only then refresh data
    if (
      !isEmpty(nowPlayingGuideItem.containerItems) &&
      !nowPlayingGuideItem.isFetching &&
      currentlyPlayingGuideId &&
      currentlyPlayingGuideId !==
        getNowPlayingContainerItemGuideId(nowPlayingGuideItem.containerItems)
    ) {
      actions.fetchNowPlayingItems(currentlyPlayingGuideId);
    }

    this.scrollTimeout = setTimeout(this.startScrolling, 2000);
  }

  componentDidUpdate(prevProps) {
    const { nowPlaying, playerStatus } = this.props;

    const previousSubtitle = getSubtitle(prevProps?.nowPlaying);
    const newSubtitle = getSubtitle(nowPlaying);
    const didPlayerTransitionToFailedState =
      prevProps.playerStatus !== playerStatus &&
      playerStatus === playerStatuses.failed;

    if (previousSubtitle !== newSubtitle || didPlayerTransitionToFailedState) {
      // using setTimeout, as the refs aren't necessarily updating immediately
      setTimeout(this.startScrolling, 0);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.scrollTimeout);
  }

  handleClose() {
    this.props.actions.closeNowPlayingDialogAndTrack();
  }

  shouldShowPrerollInfo() {
    const {
      playerStatus,
      isPrerollLoaded,
      [PAUSE_PREROLL_ENABLED]: isPausePrerollEnabled,
    } = this.props;
    if (isPausePrerollEnabled) {
      return isPrerollLoaded;
    }
    return playerStatus === playerStatuses.preroll;
  }

  startScrolling() {
    const titleInnerWidth = get(
      this.titleInnerContainer,
      'current.scrollWidth',
    );
    const titleOuterWidth = get(
      this.titleOuterContainer,
      'current.clientWidth',
    );
    const subtitleInnerWidth = get(
      this.subtitleInnerContainer,
      'current.scrollWidth',
    );
    const subtitleOuterWidth = get(
      this.subtitleOuterContainer,
      'current.clientWidth',
    );

    this.setState({
      isTitleScrolling: titleInnerWidth > titleOuterWidth,
      isSubtitleScrolling: subtitleInnerWidth > subtitleOuterWidth,
    });
  }

  processTitle() {
    const { nowPlaying } = this.props;
    const { getLocalizedText } = this.context;

    const titleTextCss = this.state.isTitleScrolling ? css.scrolling : '';
    // NOTE: Render subtitle (e.g. Radiohead - Fake Plastic Trees) below image
    // TODO: Fix naming of these title and subtitle functions to properly reflect their usage
    const title = getSubtitle(nowPlaying);
    const shouldShowPrerollInfo = this.shouldShowPrerollInfo();
    const titleText = shouldShowPrerollInfo
      ? getLocalizedText(PREROLL_TITLE)
      : title;
    const testId = shouldShowPrerollInfo ? 'PrerollTitle' : 'Title';

    return { testId, titleText, titleTextCss };
  }

  processSubtitle() {
    const { nowPlaying, playerStatus } = this.props;
    const { getLocalizedText } = this.context;

    // NOTE: Render title (e.g. ALT 92.9) in page header
    // TODO: Fix naming of these title and subtitle functions to properly reflect their usage
    const subtitle =
      playerStatus === playerStatuses.failed
        ? getLocalizedText(ERROR_MESSAGE)
        : getTitle(nowPlaying);
    const subtitleTextCss = this.state.isSubtitleScrolling ? css.scrolling : '';
    const shouldShowPrerollInfo = this.shouldShowPrerollInfo();

    const subtitleText = shouldShowPrerollInfo
      ? getLocalizedText(PREROLL_SUBTITLE)
      : subtitle;
    const testId = shouldShowPrerollInfo ? 'PrerollSubtitle' : 'Subtitle';

    return { testId, subtitleText, subtitleTextCss };
  }

  renderDesktopNowPlaying() {
    const { nowPlaying } = this.props;
    return (
      <Fragment>
        <div className={css.topSection}>
          <div className={css.actionsAndTitle} data-testid="actionsAndTitle">
            <div
              onClick={this.handleClose}
              data-testid={`${idPrefix}MinimizeContainer`}
              className={css.minimizeContainer}
            >
              <CaretDown
                key={`${idPrefix}Minimize`}
                className={css.minimizeButton}
                fill="white"
              />
            </div>
          </div>
        </div>
        <div className={css.innerDesktopContent}>
          <div className={css.flexContainer}>
            <ImageWithDefault
              id={`${idPrefix}Artwork`}
              containerClassName={css.artworkImageContainer}
              className={css.artworkImage}
              src={nowPlaying.image}
              alt={nowPlaying.title}
              renderImageWithCss
            />
            <div className={css.underImageTextContainer}>
              {this.renderDesktopTitle()}
              {this.renderDesktopSubtitle()}
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  renderMobileNowPlaying() {
    const {
      nowPlaying,
      breakpoint,
      actions,
      matchUrl,
      selectedContentId,
      upsell,
      canShowUpsell,
      isUserSubscribed,
      canChangePlaybackRate,
      hideDisplayAd,
      isExpiredWebSubscriber,
      [ENABLE_PLAYBACK_SPEED_CONTROL]: isPlaybackRateExperimentEnabled,
    } = this.props;

    const { location } = this.context;

    const playButtonSize = is360(breakpoint) ? 80 : 60;
    const shouldShowSpeedControl =
      isPlaybackRateExperimentEnabled && canChangePlaybackRate;

    return (
      <Fragment>
        <div className={css.topSection}>
          <div
            className={classNames(css.actionsAndTitle, {
              [css.spaceBelowTitle]: !canShowUpsell,
            })}
            data-testid="actionsAndTitle"
          >
            <div
              onClick={this.handleClose}
              data-testid={`${idPrefix}MinimizeContainer`}
              className={css.minimizeContainer}
            >
              <CaretDown
                key={`${idPrefix}Minimize`}
                className={css.minimizeButton}
                fill="white"
              />
            </div>
            {this.renderMobileTitle()}
            <div className={css.favoriteContainer}>
              <FavoriteControl
                iconClassName={css.heartIcon}
                unFavoritedClassName={css.unfavorited}
              />
            </div>
          </div>
          {canShowUpsell && (
            <UpsellRibbon
              onButtonClick={actions.closeNowPlayingDialog}
              buttonUrl={createSubscribeLink({
                guideId: selectedContentId,
                source: SOURCE,
                location,
              })}
              className={css.upsellRibbon}
              upsell={upsell}
              isExpiredWebSubscriber={isExpiredWebSubscriber}
            />
          )}
        </div>
        <div className={css.innerContent}>
          {!isUserSubscribed && !hideDisplayAd && (
            <NowPlayingDialogAd
              matchUrl={matchUrl}
              guideId={selectedContentId}
            />
          )}
          <ImageWithDefault
            id={`${idPrefix}Artwork`}
            containerClassName={classNames(css.artworkImageContainer, {
              [css.smallerImage]: canShowUpsell,
            })}
            className={css.artworkImage}
            src={nowPlaying.image}
            alt={nowPlaying.title}
            renderImageWithCss
          />
          {this.renderMobileSubtitle()}
          <div className={css.scrubberContainer}>
            <Scrubber isInNowPlayingDialog />
          </div>
          <div className={css.controlsContainer}>
            {shouldShowSpeedControl && (
              <SpeedControl iconContainerClass={css.speedControlContainer} />
            )}
            <PlayButton
              className={classNames(css.playButtonContainer, {
                [css.withSpeedControl]: shouldShowSpeedControl,
              })}
              customHeight={playButtonSize}
              customWidth={playButtonSize}
              breakpoint={breakpoint}
              customFill={cssVars['--secondary-color-7']}
              progressColor="white"
            />
          </div>
        </div>
      </Fragment>
    );
  }

  renderMobileTitle() {
    const { testId, titleText, titleTextCss } = this.processTitle();

    return (
      <div ref={this.titleOuterContainer} className={css.title}>
        <div className={css.leftGradient} />
        <div
          ref={this.titleInnerContainer}
          className={titleTextCss}
          data-testid={idPrefix + testId}
        >
          {titleText}
        </div>
        <div className={css.rightGradient} />
      </div>
    );
  }

  renderDesktopTitle() {
    const { testId, titleText, titleTextCss } = this.processTitle();

    return (
      <div ref={this.titleOuterContainer} className={css.title}>
        <ClampText
          clamp={1}
          ref={this.titleInnerContainer}
          className={titleTextCss}
          data-testid={idPrefix + testId}
          truncationHTML={'<span>...</span>'}
        >
          {titleText}
        </ClampText>
      </div>
    );
  }

  renderMobileSubtitle() {
    const { testId, subtitleText, subtitleTextCss } = this.processSubtitle();

    return (
      <div
        className={css.subtitle}
        ref={this.subtitleOuterContainer}
        data-testid={`${idPrefix}SubtitleContainer`}
      >
        <div className={css.leftGradient} />
        <div
          className={subtitleTextCss}
          ref={this.subtitleInnerContainer}
          data-testid={idPrefix + testId}
        >
          {subtitleText}
        </div>
        <div className={css.rightGradient} />
      </div>
    );
  }

  renderDesktopSubtitle() {
    const { testId, subtitleText, subtitleTextCss } = this.processSubtitle();

    return (
      <div
        className={css.subtitle}
        ref={this.subtitleOuterContainer}
        data-testid={`${idPrefix}SubtitleContainer`}
      >
        <ClampText
          clamp={1}
          className={subtitleTextCss}
          ref={this.subtitleInnerContainer}
          data-testid={idPrefix + testId}
          truncationHTML={'<span>...</span>'}
        >
          {subtitleText}
        </ClampText>
      </div>
    );
  }

  render() {
    const { isDesktopNowPlayingEnabled } = this.props;

    return isDesktopNowPlayingEnabled
      ? this.renderDesktopNowPlaying()
      : this.renderMobileNowPlaying();
  }
}

// exported for unit testing
export function mapStateToProps(state) {
  const upsell = selectNowPlayingUpsell(state);
  return {
    userAgent: selectUserAgent(state),
    nowPlaying: selectNowPlaying(state),
    nowPlayingGuideItem: selectNowPlayingGuideItem(state),
    playerStatus: selectPlayerStatus(state),
    isPrerollLoaded: selectIsPrerollLoaded(state),
    upsell,
    canShowUpsell: !!(upsell.canUpsell && !selectIsUserSubscribed(state)),
    isUserSubscribed: selectIsUserSubscribed(state),
    canChangePlaybackRate: selectCanChangePlaybackRate(state),
    currentlyPlayingGuideId: selectCurrentlyPlayingGuideId(state),
    isExpiredWebSubscriber: selectIsExpiredWebSubscriber(state),
    isDesktopNowPlayingEnabled: selectIsDesktopNowPlayingEnabled(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        closeNowPlayingDialogAndTrack,
        closeNowPlayingDialog,
        fetchNowPlayingItems,
      },
      dispatch,
    ),
  };
}

export default flow(
  connectWithAuth,
  connectWithExperiments([
    PAUSE_PREROLL_ENABLED,
    ENABLE_PLAYBACK_SPEED_CONTROL,
  ]),
  connect(mapStateToProps, mapDispatchToProps),
)(NowPlaying);
